import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  LuArrowLeft,
  LuBan,
  LuCode,
  LuEllipsisVertical,
  LuPencil,
  LuRefreshCw,
  LuX,
} from "react-icons/lu";
import toast from "react-hot-toast";
import SidebarLayout from "../../PageLayout/SidebarLayout";
import ConnectConnectorDrawer from "../components/ConnectConnectorDrawer";
import { getCatalogByType } from "../connectors/catalog";
import {
  fetchConnector,
  getApiErrorMessage,
  removeConnector,
  setConnectorEnabled,
  startConnectorSync,
} from "../connectors/integrationsService";
import {
  formatDateLong,
  formatNextSync,
  formatRelativeTime,
  HealthBadge,
  hostFromUrl,
  maskId,
  maskSecret,
} from "../connectors/helpers";

function OverviewCell({ label, value }) {
  return (
    <div className="rounded-lg border border-[#F6F7FA] bg-[#F6F7FA] px-2 py-3 min-w-0">
      <p className="text-[12px] leading-[14px] text-[#898F99]">{label}</p>
      <p className="text-[14px] leading-4 font-semibold text-[#031F36] mt-0.5 break-words">{value}</p>
    </div>
  );
}

function ConfigRow({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-4 py-3">
      <span className="text-[12px] leading-[14px] text-[#898F99]">{label}</span>
      <span className="text-[12px] leading-[14px] font-semibold text-[#031F36] text-right break-all">{value}</span>
    </div>
  );
}

function connectionRows(connector) {
  const cfg = connector.config || {};
  const keys = Object.keys(cfg).filter((k) => cfg[k] != null && cfg[k] !== "");
  if (connector.type === "SAP") {
    return [
      { label: "Base URL", value: hostFromUrl(cfg.baseUrl) },
      { label: "Entity Set", value: cfg.entitySet || "—" },
      { label: "Auth Type", value: cfg.authType || connector.authMethod || "—" },
      cfg.authType === "OAuth2 Client Credentials"
        ? { label: "Client Secret", value: maskSecret(cfg.clientSecret) }
        : { label: "Password", value: maskSecret(cfg.password) },
    ];
  }
  if (connector.type === "HubSpot") {
    return [
      { label: "Hub ID", value: cfg.hubId || "—" },
      { label: "Client ID", value: maskId(cfg.clientId) },
      { label: "Client Secret", value: maskSecret(cfg.clientSecret) },
      { label: "Sync Frequency", value: connector.frequency || "—" },
    ];
  }
  if (connector.type === "Salesforce" || cfg.instanceUrl || cfg.clientId) {
    return [
      { label: "Instance URL", value: hostFromUrl(cfg.instanceUrl) },
      { label: "Client ID", value: maskId(cfg.clientId) },
      { label: "Client Secret", value: maskSecret(cfg.clientSecret) },
      { label: "Sync Frequency", value: connector.frequency || "—" },
    ];
  }
  if (!keys.length) return [];
  return keys.map((key) => ({
    label: key.replace(/_/g, " ").replace(/([A-Z])/g, " $1").replace(/^\s/, "").replace(/\b\w/g, (c) => c.toUpperCase()),
    value: String(cfg[key]),
  }));
}

export default function ConnectorDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [menuOpen, setMenuOpen] = useState(false);
  const [connector, setConnector] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const catalog = connector
    ? {
        ...(getCatalogByType(connector.type) || {
          id: String(connector.type || connector.id || "connector").toLowerCase(),
          fields: [],
          testSummaryKeys: [],
        }),
        name: connector.name,
        type: connector.type,
        authMethod: connector.authMethod || getCatalogByType(connector.type)?.authMethod || "",
        authLabel: getCatalogByType(connector.type)?.authLabel || connector.authMethod || "",
        instruction:
          getCatalogByType(connector.type)?.instruction ||
          `Fields below are specific to ${connector.type}. Enter the credentials for the instance you want to connect.`,
      }
    : null;
  const editing = searchParams.get("edit") === "1";

  const goBack = () => navigate("/settings?tab=integrations");

  const load = useCallback(async () => {
    const data = await fetchConnector(id);
    if (!data?.id) {
      setConnector(null);
      setError("No connector found.");
      return;
    }
    setConnector(data);
  }, [id]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        await load();
      } catch (err) {
        if (cancelled || err?.code === "ERR_CANCELED") return;
        setConnector(null);
        setError(getApiErrorMessage(err, "This connector could not be found."));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [load]);

  const handleSync = async () => {
    if (!connector) return;
    setBusy(true);
    try {
      await startConnectorSync(connector.id);
      toast.success(`Sync started for ${connector.name}`);
      await load();
    } catch (err) {
      toast.error(getApiErrorMessage(err, `Could not start sync for ${connector.name}`));
    } finally {
      setBusy(false);
      setMenuOpen(false);
    }
  };

  const handleDisable = async () => {
    if (!connector) return;
    const enabled = !connector.enabled;
    setBusy(true);
    try {
      const updated = await setConnectorEnabled(connector.id, enabled);
      setConnector((prev) => ({
        ...prev,
        ...updated,
        enabled,
        connectionStatus: updated.connectionStatus || (enabled ? "Connected" : "Disabled"),
      }));
      toast.success(enabled ? `${connector.name} enabled` : `${connector.name} disabled`);
    } catch (err) {
      toast.error(getApiErrorMessage(err, `Could not update ${connector.name}`));
    } finally {
      setBusy(false);
      setMenuOpen(false);
    }
  };

  const handleDelete = async () => {
    if (!connector) return;
    if (!window.confirm(`Delete ${connector.name}? This cannot be undone.`)) return;
    setBusy(true);
    const toastId = toast.loading(`Deleting ${connector.name}…`);
    try {
      await removeConnector(connector.id);
      toast.success(`${connector.name} deleted`, { id: toastId });
      goBack();
    } catch (err) {
      toast.error(getApiErrorMessage(err, `Could not delete ${connector.name}`), { id: toastId });
      setBusy(false);
    } finally {
      setMenuOpen(false);
    }
  };

  if (loading) {
    return (
      <SidebarLayout pageTitle="Connector" breadcrumb="Dashboard / Settings / Connector">
        <p className="text-sm text-gray-500 p-4">Loading connector…</p>
      </SidebarLayout>
    );
  }

  if (!connector) {
    return (
      <SidebarLayout pageTitle="Connector" breadcrumb="Dashboard / Settings / Connector">
        <button type="button" onClick={goBack} className="text-sm font-medium text-[#0F172A] mb-4">
          ← Back
        </button>
        <p className="text-sm text-gray-500">{error || "No connector found."}</p>
      </SidebarLayout>
    );
  }

  const rows = connectionRows(connector);
  const failed = connector.health === "Failed";

  return (
    <SidebarLayout
      pageTitle={connector.name}
      breadcrumb={`Dashboard / Settings / ${connector.name}`}
      pageSubtitle={`${connector.connectionStatus} • ${connector.frequency} sync`}
      titleAddon={<HealthBadge health={connector.health} />}
      headerActions={
        <div className="relative">
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            disabled={busy}
            aria-busy={busy}
            className="h-9 w-9 inline-flex items-center justify-center rounded-lg border border-gray-600 text-white hover:bg-white/10"
            aria-label="Connector actions"
          >
            <LuEllipsisVertical className="h-4 w-4" />
          </button>
          {menuOpen && (
            <>
              <button
                type="button"
                className="fixed inset-0 z-40"
                aria-label="Close menu"
                onClick={() => setMenuOpen(false)}
              />
              <div className="absolute right-0 mt-2 w-44 bg-white rounded-lg shadow-md border border-[#E6E7EB] py-1 z-50 text-[#031F36]">
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => { setMenuOpen(false); handleSync(); }}
                  className="w-full px-3 py-2 text-left text-[12px] leading-[14px] hover:bg-[#F6F7FA] inline-flex items-center gap-2 disabled:opacity-50"
                >
                  <LuRefreshCw className="h-4 w-4 text-[#898F99]" /> Sync Now
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => { setMenuOpen(false); setSearchParams({ edit: "1" }); }}
                  className="w-full px-3 py-2 text-left text-[12px] leading-[14px] hover:bg-[#F6F7FA] inline-flex items-center gap-2 disabled:opacity-50"
                >
                  <LuPencil className="h-4 w-4 text-[#898F99]" /> Edit
                </button>
                <button
                  type="button"
                  onClick={() => setMenuOpen(false)}
                  className="w-full px-3 py-2 text-left text-[12px] leading-[14px] hover:bg-[#F6F7FA] inline-flex items-center gap-2"
                >
                  <LuCode className="h-4 w-4 text-[#898F99]" /> APIs
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={handleDisable}
                  className="w-full px-3 py-2 text-left text-[12px] leading-[14px] hover:bg-[#F6F7FA] inline-flex items-center gap-2 disabled:opacity-50"
                >
                  <LuBan className="h-4 w-4 text-[#898F99]" /> {connector.enabled ? "Disable" : "Enable"}
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => { setMenuOpen(false); handleDelete(); }}
                  className="w-full px-3 py-2 text-left text-[12px] leading-[14px] hover:bg-[#F6F7FA] inline-flex items-center gap-2 text-[#CD0F0F] disabled:opacity-50"
                >
                  <LuX className="h-4 w-4" /> Delete
                </button>
              </div>
            </>
          )}
        </div>
      }
    >
      <div className="p-4 md:p-6 max-w-[1400px] mx-auto w-full min-w-0">
        <button
          type="button"
          onClick={goBack}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-[#0F172A] mb-5"
        >
          <LuArrowLeft className="h-4 w-4" /> Back
        </button>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
          <div className="xl:col-span-2 space-y-4">
            <section className="bg-white border border-[#E6E7EB] rounded-lg p-5">
              <h2 className="text-[14px] leading-4 font-semibold text-[#031F36] mb-4">Overview</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <OverviewCell label="Last Sync" value={formatRelativeTime(connector.lastSync)} />
                <OverviewCell label="Version" value={connector.version || "—"} />
                <OverviewCell label="Auth Method" value={connector.authMethod || catalog?.authMethod || "—"} />
                <OverviewCell label="Next Sync" value={formatNextSync(connector)} />
                <OverviewCell label="Created By" value={connector.createdBy || "—"} />
                <OverviewCell label="Created On" value={formatDateLong(connector.createdAt || connector.createdOn)} />
              </div>
            </section>

            <section className="bg-white border border-[#E6E7EB] rounded-lg p-5">
              <h2 className="text-[14px] leading-4 font-semibold text-[#031F36] mb-2">Connection Config</h2>
              {rows.length === 0 ? (
                <p className="text-sm text-gray-500">No connection config</p>
              ) : (
                <div className="divide-y divide-[#F3F4F6]">
                  {rows.map((row) => (
                    <ConfigRow key={row.label} label={row.label} value={row.value} />
                  ))}
                </div>
              )}
            </section>
          </div>

          <div className="space-y-4">
            <section className="bg-white border border-[#E6E7EB] rounded-lg p-5">
              <h2 className="text-[14px] leading-4 font-semibold text-[#031F36] mb-3">Health</h2>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-[12px] leading-[14px] text-[#898F99]">Status</span>
                  <span
                    className={`text-[12px] leading-[14px] font-semibold ${
                      failed
                        ? "text-[#CD0F0F]"
                        : connector.health === "Warning"
                        ? "text-[#EF9F27]"
                        : connector.health === "Healthy"
                        ? "text-[#528416]"
                        : "text-[#031F36]"
                    }`}
                  >
                    {connector.health || "—"}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[12px] leading-[14px] text-[#898F99]">Uptime (30d)</span>
                  <span className="text-[12px] leading-[14px] font-semibold text-[#031F36]">
                    {connector.uptime || "—"}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[12px] leading-[14px] text-[#898F99]">Avg. Latency</span>
                  <span className="text-[12px] leading-[14px] font-semibold text-[#031F36]">
                    {connector.avgLatency || "—"}
                  </span>
                </div>
              </div>
            </section>

            <section className="bg-white border border-[#E6E7EB] rounded-lg p-5">
              <h2 className="text-[14px] leading-4 font-semibold text-[#031F36] mb-3">Latest Error</h2>
              <div className="rounded-lg border border-[#CD0F0F] bg-[#FCEBEB] p-3 flex flex-col gap-2.5">
                <p className="text-[12px] leading-[14px] font-semibold text-[#031F36]">
                  {connector.latestError || "No errors"}
                </p>
                <p className="text-[12px] leading-[14px] text-[#898F99]">
                  {connector.lastIssue
                    ? `Last recorded issue: ${connector.lastIssue}`
                    : "No recorded issue"}
                </p>
              </div>
            </section>
          </div>
        </div>
      </div>

      {editing && catalog && (
        <ConnectConnectorDrawer
          connector={catalog}
          existing={connector}
          onClose={() => setSearchParams({})}
          onSaved={() => {
            setSearchParams({});
            load().catch(() => {});
          }}
        />
      )}
    </SidebarLayout>
  );
}

